import React, {createRef} from "react";
import PropTypes from "prop-types";
import ProcessingComponent from "../../util/ProcessingComponent";
import {getFormInputCssClass} from "../../util/FormUtil";
import {ERROR_CODE_BAD_INPUT, fetchResponseHandler, fetchVoidResponseHandler} from "../../util/FetchUtil";
import UserDataService from "../common/UserDataService";

const PROP_NAME_PASSWORD = "password";

class EmailUpdateVerificationComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showPassword: false
        };

        this.hideProcessing = this.hideProcessing.bind(this);
        this.isFormValid = this.isFormValid.bind(this);
        this.setPasswordValue = this.setPasswordValue.bind(this);
        this.setVerificationCode = this.setVerificationCode.bind(this);
        this.showProcessing = this.showProcessing.bind(this);
        this.showPassword = this.showPassword.bind(this);
        this.toggleShowPassword = this.toggleShowPassword.bind(this);
        this.verifyEmailCodeAndPassword = this.verifyEmailCodeAndPassword.bind(this);
        this.securityCodeInput = createRef();
    }

    componentDidMount() {
        const {email, userDataService, onError, onCancel} = this.props;
        this.showProcessing();

        userDataService.sendCodeToEmail({email})
        .then(fetchResponseHandler)
        .then(data => {
            const {verificationCodeSent} = data;
            this.setState(state => ({...state, verificationCodeSent}));
        }).catch(e => {
            if (e.errorCode === ERROR_CODE_BAD_INPUT) {
                // cancel out from this screen, if it's bad input so user can correct it. Technically, shouldn't
                // even pass the first screen, but just in case.
                onCancel();
            }
            onError(e);
        }).finally(() => {
            this.hideProcessing();
            if (this.securityCodeInput && this.securityCodeInput.current){
                this.securityCodeInput.current.focus();
            }
        });
    }

    showProcessing(){
        this.setState(state => ({...state, showProcessing: true}));
    }

    hideProcessing(){
        this.setState(state => ({...state, showProcessing: false}));
    }

    isFormValid(){
        const {password, verificationCode} = this.state;
        const hasVerificationCode = verificationCode && verificationCode.trim().length > 0;
        const hasPassword = password && password.trim().length > 0;
        return hasVerificationCode && hasPassword;
    }

    setPasswordValue(e){
        const password = e.target.value;
        this.setState(state => ({...state, password, invalidPassword: false }));
    }

    setVerificationCode(e){
        const verificationCode = e.target.value;
        this.setState(state => ({...state, verificationCode, invalidCode: false}));
    }

    showPassword(show){
        this.setState(state => ({...state, showPassword: show }));
    }

    toggleShowPassword() {
        this.showPassword(!this.state.showPassword);
    }

    verifyEmailCodeAndPassword(e){
        if (e) { e.preventDefault(); }
        if (!this.isFormValid()){ return; } // don't do anything if form is not valid
        this.showProcessing();

        const { password, verificationCode } = this.state;
        const { userDataService, onError, onSuccess } = this.props;

        this.setState(state => ({...state, invalidCode: false, invalidPassword: false}));

        userDataService.verifyEmailCodeAndPassword({ code: verificationCode, password })
            .then(fetchResponseHandler)
            .then(data => {
                if (data.success){
                    onSuccess(data.verificationReceipt);
                    return;
                }
                this.setState(state => ({...state, invalidCode: data.invalidCode, invalidPassword: data.invalidPassword }));
            })
            .catch(onError)
            .finally(this.hideProcessing);
    }

    getMessage(email, oldEmailSameAsUserName) {
        return "Enter your password and the security code that was sent to your email (" + email + "). " +
            (oldEmailSameAsUserName ? "By changing the email, the user name will also be updated. " : "") +
            "If you are unable to receive the security code, please try again later.";
    }

    render(){
        const {invalidCode, invalidPassword, password, showPassword, showProcessing, verificationCode} = this.state;
        const {email, oldEmailSameAsUserName, onCancel} = this.props;
        const submitDisabled = ! this.isFormValid();

        let hasInputClassPassword = (password && password.trim().length > 0) ? " is-valid" : "";
        let passwordDisplay = showPassword ? "text" : "password";
        const overviewMessage = this.getMessage(email, oldEmailSameAsUserName);


        return (
            <div className="grid form-fields">

                <div className="grid__col-md-12 grid__col-sm-12 push--down">
                    {overviewMessage}
                </div>

                <br/>

                <div className="grid__col-md-8 grid__col-sm-12">
                    <label htmlFor={PROP_NAME_PASSWORD}>Password</label>
                    <input id={PROP_NAME_PASSWORD}
                           type={passwordDisplay}
                           style={{maxHeight: '28px'}}
                           className={"text-input text-input--large password-input-with-eye "
                                + passwordDisplay + hasInputClassPassword + (invalidPassword ? " has-error" : "")}
                           value={password}
                           onChange={this.setPasswordValue}
                           placeholder="Password"
                    />
                    <i className={"fa fa-eye-password-input"} onClick={this.toggleShowPassword} />
                    {
                        invalidPassword &&
                        <div className="error">The password you entered does not match our record.</div>
                    }
                </div>

                <br/>

                <div className="grid__col-md-12 grid__col-sm-12">
                    <label htmlFor="verificationCode">Verification Code</label>
                    <input id="verificationCode"
                           type="text"
                           className={"text-input text-input--large responsive max-width--40" + (invalidCode ? " has-error" : "")}
                           maxLength={8}
                           value={verificationCode}
                           onChange={this.setVerificationCode}
                           placeholder="Verification code"
                           ref={this.securityCodeInput}
                    />
                {
                    invalidCode &&
                    <div className="error">The verification code you entered does not match our record.</div>
                }

                    <br/>

                    <div className="text--center push--top">
                        <button className="btn btn--large btn--link" onClick={onCancel} >Cancel</button>
                        <button
                            type="submit"
                            disabled={submitDisabled}
                            onClick={this.verifyEmailCodeAndPassword}
                            className="btn btn--large push-half--left" >Continue</button>
                    </div>
                    <ProcessingComponent show={showProcessing}/>
                </div>
            </div>
        );
    }

}

EmailUpdateVerificationComponent.prototypes = {
    email: PropTypes.object.isRequired,
    oldEmailSameAsUserName: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    userDataService: PropTypes.instanceOf(UserDataService).isRequired,
}

export default EmailUpdateVerificationComponent;